<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { Entity } from "@/services";
import { useMainStore } from "@/stores/main.js";
import { mapPeriodToDateRange } from "@/utils";
import MHeader from "@/components/m/MHeader.vue";
import MIcon from "@/components/m/MIcon.vue";
import MPeriodScope from "@/components/m/MPeriodScope.vue";
import MPullScroll from "@/components/m/MPullScroll.vue";
import MSheet from "@/components/m/MSheet.vue";
import MStars from "@/components/m/MStars.vue";
import MToast from "@/components/m/MToast.vue";
import BtAreaTrend from "@/components/bt/BtAreaTrend.vue";

const mainStore = useMainStore();

const TYPES = [
  { key: "PRODUCT", label: "Products/Services" },
  { key: "PERSON", label: "People" },
  { key: "PLACE", label: "Places" },
];

const SORT_OPTIONS = [
  ["Mention count", "count"],
  ["Sentiment (best first)", "sentiment"],
];

const REVIEW_PER_PAGE = 12;

const activeType = ref("PRODUCT");
const entities = ref([]);
const loading = ref(false);
const search = ref("");
const sortBy = ref("count");
const sortOpen = ref(false);

const selLabel = ref(null);
const trend = ref(null);
const samples = ref([]);
const reviewPage = ref(1);
const reviewTotal = ref(0);
const loadingMoreReviews = ref(false);

// Curation: 'view' shows the entity detail; the others swap the sheet body
// for the relevant inline form. Create is a separate sheet (not tied to a
// selection).
const detailMode = ref("view");
const createOpen = ref(false);
const createForm = ref({ label: "", type: "PRODUCT" });
const createError = ref("");
const editForm = ref({ id: null, label: "", type: "PRODUCT" });
const mergeTarget = ref("");
const saving = ref(false);

const toastMsg = ref("");
function flash(msg) {
  toastMsg.value = msg;
  setTimeout(() => (toastMsg.value = ""), 2200);
}

// Honour the globally-selected location so entity data matches the rest of the
// app (desktop EntitiesView does the same); "ALL" means company-wide.
const locationId = computed(() => {
  const loc = mainStore.selectedLocation;
  if (!loc || loc.location_id === "ALL") return null;
  return loc.location_id ?? null;
});

const sortText = computed(
  () =>
    SORT_OPTIONS.find(([, v]) => v === sortBy.value)?.[0] || "Mention count",
);

// Mirror desktop's sortedEntities: text filter + count/sentiment sort.
const list = computed(() => {
  let arr = [...entities.value];
  const q = search.value.trim().toLowerCase();
  if (q) {
    arr = arr.filter((e) =>
      String(e.label || "")
        .toLowerCase()
        .includes(q),
    );
  }
  if (sortBy.value === "sentiment") {
    arr.sort(
      (a, b) =>
        (b.positive || 0) -
        (b.negative || 0) -
        ((a.positive || 0) - (a.negative || 0)),
    );
  } else {
    arr.sort((a, b) => (b.mention_count || 0) - (a.mention_count || 0));
  }
  return arr;
});

const sel = computed(
  () => entities.value.find((e) => e.label === selLabel.value) || null,
);

function sentimentBarSegs(entity) {
  const total =
    (entity.positive || 0) + (entity.neutral || 0) + (entity.negative || 0);
  if (!total) return [];
  return [
    {
      pct: Math.round(((entity.positive || 0) / total) * 100),
      color: "var(--pos)",
    },
    {
      pct: Math.round(((entity.neutral || 0) / total) * 100),
      color: "var(--neu)",
    },
    {
      pct: Math.round(((entity.negative || 0) / total) * 100),
      color: "var(--neg)",
    },
  ];
}

// Per-row sentiment label (Positive / Negative / Mixed) — mirrors desktop.
function sentimentLabel(entity) {
  const total =
    (entity.positive || 0) + (entity.neutral || 0) + (entity.negative || 0);
  if (!total) return "No data";
  const pPos = Math.round(((entity.positive || 0) / total) * 100);
  const pNeg = Math.round(((entity.negative || 0) / total) * 100);
  if (pPos > pNeg + 20) return "Positive";
  if (pNeg > pPos + 20) return "Negative";
  return "Mixed";
}

function sentimentClass(entity) {
  const lbl = sentimentLabel(entity);
  if (lbl === "Positive") return "pos";
  if (lbl === "Negative") return "neg";
  return "neu";
}

const statTiles = computed(() => {
  const e = sel.value;
  if (!e) return [];
  return [
    { label: "Mentions", value: Number(e.mention_count || 0).toLocaleString() },
    {
      label: "Positive",
      value: e.positive != null ? `${e.positive}` : "-",
      tone: "var(--pos)",
    },
    {
      label: "Negative",
      value: e.negative != null ? `${e.negative}` : "-",
      tone: "var(--neg)",
    },
    {
      label: "Last Seen",
      value: e.last_mentioned
        ? new Date(e.last_mentioned).toLocaleDateString("en-GB", {
            day: "numeric",
            month: "short",
          })
        : "-",
    },
  ];
});

const sentimentBreakdown = computed(() => {
  const e = sel.value;
  if (!e) return [];
  const pos = Number(e.positive || 0);
  const neu = Number(e.neutral || 0);
  const neg = Number(e.negative || 0);
  const total = pos + neu + neg;
  const pct = (n) => (total ? Math.round((n / total) * 100) : 0);
  return [
    { label: "Positive", count: pos, pct: pct(pos), color: "var(--pos)" },
    { label: "Neutral", count: neu, pct: pct(neu), color: "var(--neu)" },
    { label: "Negative", count: neg, pct: pct(neg), color: "var(--neg)" },
  ];
});

const trendChart = computed(() => {
  const t = trend.value;
  if (!t) return { labels: [], volumes: [], sentiments: [] };
  return {
    labels: t.dates || [],
    volumes: t.series?.find((s) => s.name === "Mentions")?.data || [],
    sentiments: t.series?.find((s) => s.name === "Sentiment")?.data || [],
  };
});

const hasMoreReviews = computed(() => samples.value.length < reviewTotal.value);

function reviewSentimentClass(s) {
  if (s > 0) return "pos";
  if (s < 0) return "neg";
  return "neu";
}

function reviewSentimentLabel(s) {
  if (s > 0) return "Positive";
  if (s < 0) return "Negative";
  return "Neutral";
}

async function fetchEntities() {
  if (!mainStore.activeCompanyId) return;
  loading.value = true;
  try {
    const { startDate, endDate } = mapPeriodToDateRange(
      mainStore.selectedPeriod,
      mainStore.customRange,
    );
    const data = await Entity.list(
      mainStore.activeCompanyId,
      activeType.value,
      startDate.toISOString(),
      endDate.toISOString(),
      locationId.value,
    );
    entities.value = data?.items || [];
  } finally {
    loading.value = false;
  }
}

async function fetchTrend() {
  const e = sel.value;
  const companyId = mainStore.activeCompanyId;
  if (!e || !companyId) {
    trend.value = null;
    return;
  }
  const { startDate, endDate } = mapPeriodToDateRange(
    mainStore.selectedPeriod,
    mainStore.customRange,
  );
  trend.value = await Entity.trend(
    companyId,
    activeType.value,
    e.label,
    (mainStore.selectedAggregation || "monthly").toLowerCase(),
    startDate.toISOString(),
    endDate.toISOString(),
    locationId.value,
  );
}

async function fetchSamples(append = false) {
  const e = sel.value;
  const companyId = mainStore.activeCompanyId;
  if (!e || !companyId) {
    samples.value = [];
    reviewTotal.value = 0;
    return;
  }
  const { startDate, endDate } = mapPeriodToDateRange(
    mainStore.selectedPeriod,
    mainStore.customRange,
  );
  if (append) loadingMoreReviews.value = true;
  try {
    const data = await Entity.reviews(
      companyId,
      activeType.value,
      e.label,
      reviewPage.value,
      REVIEW_PER_PAGE,
      startDate.toISOString(),
      endDate.toISOString(),
      locationId.value,
    );
    const items = data?.items || [];
    samples.value = append ? [...samples.value, ...items] : items;
    reviewTotal.value =
      data?.total ?? (append ? reviewTotal.value : items.length);
  } finally {
    if (append) loadingMoreReviews.value = false;
  }
}

function loadMoreReviews() {
  if (loadingMoreReviews.value || !hasMoreReviews.value) return;
  reviewPage.value += 1;
  fetchSamples(true);
}

async function fetchDetail() {
  reviewPage.value = 1;
  trend.value = null;
  samples.value = [];
  reviewTotal.value = 0;
  await Promise.all([fetchTrend(), fetchSamples(false)]);
}

function openEntity(label) {
  selLabel.value = label;
  detailMode.value = "view";
  fetchDetail();
}

// --- Curation ---
function openCreate() {
  createForm.value = { label: "", type: activeType.value };
  createError.value = "";
  createOpen.value = true;
}

async function saveCreate() {
  if (!createForm.value.label.trim()) {
    createError.value = "Label is required";
    return;
  }
  createError.value = "";
  saving.value = true;
  try {
    await Entity.create(
      mainStore.selectedCompany.company_id,
      createForm.value.type,
      createForm.value.label.trim(),
    );
    flash("Entity created");
    createOpen.value = false;
    if (createForm.value.type === activeType.value) await fetchEntities();
  } catch (error) {
    if (error.response?.status === 409) {
      createError.value =
        error.response.data?.message ||
        "An entity with this name already exists.";
      return;
    }
    createError.value =
      error.response?.data?.message || "Could not create entity.";
  } finally {
    saving.value = false;
  }
}

function openEdit() {
  const e = sel.value;
  if (!e) return;
  editForm.value = { id: e.id, label: e.label, type: activeType.value };
  detailMode.value = "edit";
}

async function saveEdit() {
  if (!editForm.value.label.trim()) {
    flash("Label is required");
    return;
  }
  saving.value = true;
  try {
    await Entity.update(
      editForm.value.id,
      mainStore.selectedCompany.company_id,
      editForm.value.type,
      editForm.value.label.trim(),
    );
    flash("Entity updated");
    selLabel.value = editForm.value.label.trim();
    detailMode.value = "view";
    await fetchEntities();
    await fetchDetail();
  } catch (error) {
    flash(error.response?.data?.message || "Could not update entity.");
  } finally {
    saving.value = false;
  }
}

function openMerge() {
  mergeTarget.value = "";
  detailMode.value = "merge";
}

async function saveMerge() {
  const source = sel.value;
  const target = entities.value.find((e) => e.label === mergeTarget.value);
  if (!source || !target) {
    flash("Select a merge target");
    return;
  }
  if (source.id === target.id) {
    flash("Cannot merge entity into itself");
    return;
  }
  saving.value = true;
  try {
    await Entity.merge(
      mainStore.selectedCompany.company_id,
      source.id,
      target.id,
    );
    flash(`Merged into "${target.label}"`);
    selLabel.value = target.label;
    detailMode.value = "view";
    await fetchEntities();
    await fetchDetail();
  } catch (error) {
    flash(error.response?.data?.message || "Could not merge entity.");
  } finally {
    saving.value = false;
  }
}

async function confirmDelete() {
  const e = sel.value;
  if (!e) return;
  saving.value = true;
  try {
    await Entity.remove(e.id, mainStore.selectedCompany.company_id);
    flash("Entity deleted");
    selLabel.value = null;
    await fetchEntities();
  } catch (error) {
    flash(error.response?.data?.message || "Could not delete entity.");
  } finally {
    saving.value = false;
  }
}

const mergeOptions = computed(() =>
  entities.value.filter((e) => e.label !== sel.value?.label),
);

watch(
  () => [
    mainStore.dateFilterKey,
    locationId.value,
    mainStore.selectedAggregation,
  ],
  () => {
    selLabel.value = null;
    fetchEntities();
  },
);
watch(activeType, () => {
  entities.value = [];
  selLabel.value = null;
  fetchEntities();
});
onMounted(fetchEntities);
</script>

<template>
  <div class="btm-app">
    <MHeader title="Entities" sub="Products, people, and places" />
    <MPeriodScope />

    <!-- Type tabs -->
    <div class="btm-rail" style="padding-bottom: 4px">
      <button
        v-for="t in TYPES"
        :key="t.key"
        class="btm-chip"
        :class="{ on: activeType === t.key }"
        @click="activeType = t.key"
      >
        {{ t.label }}
      </button>
    </div>

    <!-- Search + sort + add -->
    <div
      class="btm-pad"
      style="display: flex; gap: 8px; padding-top: 0; padding-bottom: 0"
    >
      <label
        style="
          flex: 1;
          min-width: 0;
          display: flex;
          align-items: center;
          gap: 8px;
          background: var(--surface-2);
          border: 1px solid var(--border);
          border-radius: var(--pill);
          padding: 0 12px;
          height: 38px;
        "
      >
        <MIcon name="search" :size="15" />
        <input
          v-model="search"
          type="search"
          inputmode="search"
          placeholder="Search entities…"
          aria-label="Search entities"
          style="
            flex: 1;
            min-width: 0;
            border: 0;
            background: transparent;
            outline: none;
            font-family: inherit;
            font-size: 13.5px;
            color: var(--text);
          "
        />
        <button
          v-if="search"
          type="button"
          aria-label="Clear search"
          style="
            border: 0;
            background: transparent;
            color: var(--text-3);
            cursor: pointer;
            padding: 0;
            line-height: 1;
          "
          @click="search = ''"
        >
          ✕
        </button>
      </label>
      <button
        type="button"
        class="btm-chip"
        style="height: 38px; flex: 0 0 auto"
        aria-label="Sort entities"
        @click="sortOpen = true"
      >
        <MIcon name="filter" :size="14" />Sort
      </button>
      <button
        v-if="!mainStore.isReadOnly"
        type="button"
        class="btm-chip"
        style="height: 38px; flex: 0 0 auto"
        aria-label="Add entity"
        @click="openCreate"
      >
        <MIcon name="plus" :size="15" />
      </button>
    </div>

    <MPullScroll :busy="loading" @refresh="fetchEntities">
      <div class="btm-pad" style="padding-bottom: 16px">
        <div class="btm-eyebrow" style="padding: 8px 2px 6px">
          {{ list.length }} {{ list.length === 1 ? "entity" : "entities" }}
        </div>
        <div class="btm-card">
          <div
            v-for="(entity, i) in list"
            :key="entity.label"
            style="padding: 13px 14px; cursor: pointer"
            :style="{ borderTop: i ? '1px solid var(--border)' : 'none' }"
            @click="openEntity(entity.label)"
          >
            <div
              style="
                display: flex;
                align-items: center;
                gap: 10px;
                margin-bottom: 6px;
              "
            >
              <div
                style="
                  flex: 1;
                  min-width: 0;
                  font-weight: 700;
                  font-size: 13.5px;
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;
                "
              >
                {{ entity.label }}
              </div>
              <span
                :class="['btm-tag', sentimentClass(entity)]"
                style="flex: 0 0 auto"
              >
                {{ sentimentLabel(entity) }}
              </span>
            </div>
            <!-- Sentiment bar -->
            <div
              style="
                height: 4px;
                border-radius: 2px;
                display: flex;
                gap: 1px;
                overflow: hidden;
              "
            >
              <span
                v-for="seg in sentimentBarSegs(entity)"
                :key="seg.color"
                :style="{
                  width: seg.pct + '%',
                  background: seg.color,
                  borderRadius: '2px',
                }"
              />
            </div>
            <div
              class="btm-muted"
              style="
                font-size: 10.5px;
                margin-top: 4px;
                display: flex;
                gap: 6px;
              "
            >
              <span
                >{{
                  Number(entity.mention_count || 0).toLocaleString()
                }}
                mentions</span
              >
              <span v-if="entity.last_mentioned" style="color: var(--text-3)">
                ·
                {{
                  new Date(entity.last_mentioned).toLocaleDateString("en", {
                    day: "numeric",
                    month: "short",
                  })
                }}
              </span>
            </div>
          </div>
          <div v-if="!list.length && !loading" class="btm-cardpad btm-muted">
            {{
              search
                ? "No entities match your search."
                : "No entities in this period."
            }}
          </div>
          <div v-if="loading && !list.length" class="btm-cardpad btm-muted">
            Loading entities…
          </div>
        </div>
      </div>
    </MPullScroll>

    <!-- Sort picker sheet -->
    <MSheet :open="sortOpen" label="Sort by" @close="sortOpen = false">
      <div style="font-weight: 800; font-size: 16px; margin-bottom: 6px">
        Sort by
      </div>
      <button
        v-for="[label, value] in SORT_OPTIONS"
        :key="value"
        type="button"
        class="btm-trow"
        style="
          width: 100%;
          justify-content: space-between;
          background: none;
          border: 0;
          border-top: 1px solid var(--border);
          font-family: inherit;
          font-size: 14px;
          color: var(--text);
          cursor: pointer;
        "
        @click="
          sortBy = value;
          sortOpen = false;
        "
      >
        <span>{{ label }}</span>
        <MIcon v-if="sortBy === value" name="check" :size="18" />
      </button>
    </MSheet>

    <!-- Create entity sheet -->
    <MSheet :open="createOpen" label="Add entity" @close="createOpen = false">
      <div style="font-weight: 800; font-size: 17px; margin-bottom: 2px">
        Add Entity
      </div>
      <div class="btm-muted" style="margin-bottom: 14px; font-size: 12.5px">
        Track a new product, person, or place in your reviews.
      </div>
      <input
        v-model="createForm.label"
        class="ent-input"
        placeholder="Entity name"
        @keyup.enter="saveCreate"
      />
      <select
        v-model="createForm.type"
        class="ent-input"
        style="margin-top: 10px"
      >
        <option v-for="t in TYPES" :key="t.key" :value="t.key">
          {{ t.label }}
        </option>
      </select>
      <div
        v-if="createError"
        style="
          margin-top: 10px;
          padding: 10px 12px;
          border-radius: 10px;
          background: var(--neg-soft);
          color: var(--neg);
          font-size: 12.5px;
        "
      >
        {{ createError }}
      </div>
      <div style="display: flex; gap: 10px; margin-top: 16px">
        <button class="btm-btn btm-btn-ghost" @click="createOpen = false">
          Cancel
        </button>
        <button
          class="btm-btn btm-btn-primary"
          :disabled="saving"
          @click="saveCreate"
        >
          <MIcon name="check" :size="16" />Save
        </button>
      </div>
    </MSheet>

    <!-- Detail sheet -->
    <MSheet
      :open="selLabel != null"
      label="Entity detail"
      @close="selLabel = null"
    >
      <div v-if="sel">
        <div
          style="
            display: flex;
            align-items: flex-start;
            gap: 10px;
            margin-bottom: 14px;
          "
        >
          <div style="flex: 1; min-width: 0">
            <div
              style="
                display: flex;
                align-items: center;
                gap: 8px;
                flex-wrap: wrap;
                row-gap: 6px;
              "
            >
              <span
                style="
                  font-size: 18px;
                  font-weight: 800;
                  letter-spacing: -0.02em;
                "
                >{{ sel.label }}</span
              >
              <span :class="['btm-tag', sentimentClass(sel)]">{{
                sentimentLabel(sel)
              }}</span>
            </div>
            <div class="btm-muted" style="margin-top: 2px">
              {{ Number(sel.mention_count || 0).toLocaleString() }} mentions
            </div>
          </div>
        </div>

        <!-- Curation action buttons -->
        <div
          v-if="!mainStore.isReadOnly && detailMode === 'view'"
          style="display: flex; gap: 8px; margin-bottom: 16px"
        >
          <button class="btm-btn btm-btn-ghost btm-btn-sm" @click="openEdit">
            <MIcon name="dots" :size="14" />Edit
          </button>
          <button class="btm-btn btm-btn-ghost btm-btn-sm" @click="openMerge">
            <MIcon name="copy" :size="14" />Merge
          </button>
          <button
            class="btm-btn btm-btn-ghost btm-btn-sm"
            style="color: var(--neg)"
            @click="detailMode = 'delete'"
          >
            <MIcon name="alert" :size="14" />Delete
          </button>
        </div>

        <!-- EDIT form -->
        <div v-if="detailMode === 'edit'" style="margin-bottom: 8px">
          <div style="font-weight: 800; font-size: 15px; margin-bottom: 8px">
            Edit Entity
          </div>
          <input
            v-model="editForm.label"
            class="ent-input"
            placeholder="Entity name"
          />
          <select
            v-model="editForm.type"
            class="ent-input"
            style="margin-top: 10px"
          >
            <option v-for="t in TYPES" :key="t.key" :value="t.key">
              {{ t.label }}
            </option>
          </select>
          <div style="display: flex; gap: 10px; margin-top: 16px">
            <button class="btm-btn btm-btn-ghost" @click="detailMode = 'view'">
              Cancel
            </button>
            <button
              class="btm-btn btm-btn-primary"
              :disabled="saving"
              @click="saveEdit"
            >
              <MIcon name="check" :size="16" />Update
            </button>
          </div>
        </div>

        <!-- MERGE form -->
        <div v-else-if="detailMode === 'merge'" style="margin-bottom: 8px">
          <div style="font-weight: 800; font-size: 15px; margin-bottom: 4px">
            Merge Entity
          </div>
          <div class="btm-muted" style="font-size: 12.5px; margin-bottom: 10px">
            Merge "{{ sel.label }}" into another entity. The source will be
            deleted.
          </div>
          <select v-model="mergeTarget" class="ent-input">
            <option value="">— Select target entity —</option>
            <option v-for="e in mergeOptions" :key="e.label" :value="e.label">
              {{ e.label }}
            </option>
          </select>
          <div style="display: flex; gap: 10px; margin-top: 16px">
            <button class="btm-btn btm-btn-ghost" @click="detailMode = 'view'">
              Cancel
            </button>
            <button
              class="btm-btn btm-btn-primary"
              :disabled="saving"
              @click="saveMerge"
            >
              <MIcon name="check" :size="16" />Merge
            </button>
          </div>
        </div>

        <!-- DELETE confirm -->
        <div v-else-if="detailMode === 'delete'" style="margin-bottom: 8px">
          <div style="font-weight: 800; font-size: 15px; margin-bottom: 4px">
            Delete "{{ sel.label }}"?
          </div>
          <div class="btm-muted" style="font-size: 12.5px; margin-bottom: 10px">
            This will permanently remove this entity and cannot be undone.
          </div>
          <div style="display: flex; gap: 10px">
            <button class="btm-btn btm-btn-ghost" @click="detailMode = 'view'">
              Cancel
            </button>
            <button
              class="btm-btn btm-btn-primary"
              style="background: var(--neg); border-color: var(--neg)"
              :disabled="saving"
              @click="confirmDelete"
            >
              <MIcon name="alert" :size="16" />Delete
            </button>
          </div>
        </div>

        <!-- VIEW: overview -->
        <template v-else>
          <!-- Stat tiles -->
          <div
            style="
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 10px;
              margin-bottom: 16px;
            "
          >
            <div
              v-for="tile in statTiles"
              :key="tile.label"
              class="btm-card btm-cardpad"
              style="padding: 12px 14px"
            >
              <div
                class="btm-muted"
                style="
                  font-size: 10.5px;
                  text-transform: uppercase;
                  letter-spacing: 0.04em;
                "
              >
                {{ tile.label }}
              </div>
              <div
                style="font-size: 19px; font-weight: 800; margin-top: 3px"
                :style="{ color: tile.tone || 'var(--text)' }"
              >
                {{ tile.value }}
              </div>
            </div>
          </div>

          <!-- Sentiment breakdown -->
          <div class="btm-card btm-cardpad" style="margin-bottom: 16px">
            <div
              style="
                height: 10px;
                border-radius: 5px;
                display: flex;
                gap: 1px;
                overflow: hidden;
              "
            >
              <span
                v-for="seg in sentimentBreakdown"
                :key="seg.label"
                :style="{ width: seg.pct + '%', background: seg.color }"
              />
            </div>
            <div
              style="
                display: flex;
                flex-wrap: wrap;
                gap: 8px 18px;
                margin-top: 11px;
                font-size: 12px;
              "
            >
              <span
                v-for="seg in sentimentBreakdown"
                :key="seg.label"
                style="display: flex; align-items: center; gap: 6px"
              >
                <span
                  :style="{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: seg.color,
                  }"
                />
                <span style="color: var(--text-2)">{{ seg.label }}</span>
                <b style="font-weight: 700">{{ seg.count.toLocaleString() }}</b>
                <span class="btm-muted">{{ seg.pct }}%</span>
              </span>
            </div>
          </div>

          <!-- Trend chart -->
          <div class="btm-card btm-cardpad" style="margin-bottom: 16px">
            <div class="btm-cardhead">
              <div>
                <div class="t" style="font-size: 14px">
                  Mentions & Sentiment
                </div>
              </div>
              <div style="margin-left: auto; display: flex; gap: 8px">
                <span class="btm-tag brand" style="background: transparent">
                  <span
                    style="
                      width: 7px;
                      height: 7px;
                      border-radius: 50%;
                      background: var(--brand);
                    "
                  />Vol
                </span>
                <span class="btm-tag pos" style="background: transparent">
                  <span
                    style="
                      width: 7px;
                      height: 7px;
                      border-radius: 50%;
                      background: var(--pos);
                    "
                  />Sent
                </span>
              </div>
            </div>
            <BtAreaTrend
              v-if="trendChart.volumes.length"
              :volumes="trendChart.volumes"
              :sentiments="trendChart.sentiments"
              :labels="trendChart.labels"
              :height="200"
            />
            <div
              v-else
              class="btm-muted"
              style="padding: 30px 0; text-align: center"
            >
              {{ trend ? "No trend data." : "Loading trend…" }}
            </div>
          </div>

          <!-- Sample reviews (paginated) -->
          <div class="btm-cardhead" style="margin-bottom: 8px">
            <div class="t" style="font-size: 14px">Mentions</div>
            <span
              v-if="reviewTotal"
              class="btm-muted btm-mono"
              style="margin-left: auto; font-size: 11px"
            >
              {{ reviewTotal.toLocaleString() }}
            </span>
          </div>
          <div class="btm-card btm-cardpad" style="margin-bottom: 16px">
            <div
              v-for="(r, i) in samples"
              :key="r.review_id || i"
              class="btm-trow"
            >
              <div style="flex: 1; min-width: 0">
                <div
                  style="
                    display: flex;
                    align-items: center;
                    gap: 7px;
                    margin-bottom: 2px;
                  "
                >
                  <MStars :value="r.rating" :size="11" />
                  <span
                    v-if="
                      r.entity_sentiment !== undefined &&
                      r.entity_sentiment !== null
                    "
                    :class="[
                      'btm-tag',
                      reviewSentimentClass(r.entity_sentiment),
                    ]"
                    style="font-size: 10px"
                    >{{ reviewSentimentLabel(r.entity_sentiment) }}</span
                  >
                  <span
                    v-if="r.review_created"
                    class="btm-muted"
                    style="font-size: 10px; margin-left: auto"
                    >{{
                      new Date(r.review_created).toLocaleDateString("en", {
                        day: "numeric",
                        month: "short",
                      })
                    }}</span
                  >
                </div>
                <div
                  style="
                    font-size: 12px;
                    color: var(--text-2);
                    line-height: 1.4;
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                  "
                >
                  {{ r.content }}
                </div>
              </div>
            </div>
            <div v-if="!samples.length" class="btm-muted">
              No reviews mention this entity.
            </div>
            <div
              v-if="loadingMoreReviews"
              class="btm-muted"
              style="padding: 10px 0 2px; text-align: center; font-size: 12px"
            >
              Loading more…
            </div>
            <button
              v-else-if="hasMoreReviews && samples.length"
              type="button"
              class="btm-btn btm-btn-ghost btm-btn-sm"
              style="margin: 10px auto 2px; width: auto; padding: 0 16px"
              @click="loadMoreReviews"
            >
              Load more
            </button>
          </div>
        </template>
      </div>
    </MSheet>

    <MToast :msg="toastMsg" :show="!!toastMsg" />
  </div>
</template>

<style scoped>
.ent-input {
  width: 100%;
  box-sizing: border-box;
  height: 42px;
  padding: 0 13px;
  border-radius: 12px;
  background: var(--surface-2);
  border: 1px solid var(--border);
  font-family: inherit;
  font-size: 14px;
  color: var(--text);
  outline: none;
}
.ent-input:focus {
  border-color: var(--brand-line);
}
select.ent-input {
  appearance: none;
  -webkit-appearance: none;
}
</style>
